import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Drawer } from 'antd';
import { throttle } from 'lodash';
import _style from './CsDraggableDrawer.scss';

// 将CSS表达式转换为像素值的辅助函数
const parseWidthToPx = (widthValue) => {
  if (typeof widthValue === 'number') {
    return widthValue;
  }

  if (typeof widthValue === 'string') {
    // 如果是纯数字字符串，直接转换
    if (/^\d+$/.test(widthValue)) {
      return parseInt(widthValue);
    }

    // 如果是像素值，提取数字
    if (widthValue.endsWith('px')) {
      return parseInt(widthValue);
    }

    // 如果是百分比或vw，计算实际像素值
    if (widthValue.endsWith('%')) {
      const percent = parseFloat(widthValue) / 100;
      return Math.floor(window.innerWidth * percent);
    }

    if (widthValue.endsWith('vw')) {
      const vw = parseFloat(widthValue) / 100;
      return Math.floor(window.innerWidth * vw);
    }

    // 处理CSS max()函数
    if (widthValue.includes('max(')) {
      const match = widthValue.match(/max\(([^)]+)\)/);
      if (match) {
        const values = match[1].split(',').map(v => v.trim());
        const parsedValues = values.map(v => parseWidthToPx(v));
        return Math.max(...parsedValues);
      }
    }

    // 处理CSS min()函数
    if (widthValue.includes('min(')) {
      const match = widthValue.match(/min\(([^)]+)\)/);
      if (match) {
        const values = match[1].split(',').map(v => v.trim());
        const parsedValues = values.map(v => parseWidthToPx(v));
        return Math.min(...parsedValues);
      }
    }

    // 其他情况，尝试直接返回
    return 320; // 默认值
  }

  return 320; // 默认值
};

export const CsDraggableDrawer = (props) => {
  const {
    open,
    onWidthChange,
    width: propWidth = 320,
    placement = 'right',
    disableDrag = false,
    ...otherProps
  } = props;

  const [width, setWidth] = useState(() => parseWidthToPx(propWidth));
  const [isDragging, setIsDragging] = useState(false);
  const drawerRef = useRef(null);
  const resizeHandleRef = useRef(null);
  const currentWidthRef = useRef(parseWidthToPx(propWidth));

  // 打开抽屉的时候，如果当前宽度过宽，强制缩小到0.8倍innerWidth
  useEffect(() => {
    if (open && width > window.innerWidth * 0.8) {
      onWidthChange(window.innerWidth * 0.8);
    }
  }, [open]);

  // 创建throttled版本的onWidthChange回调
  const throttledOnWidthChange = useMemo(() => {
    if (!onWidthChange) return null;
    return throttle((newWidth) => {
      onWidthChange(newWidth);
    }, 16); // 16ms throttle
  }, [onWidthChange]);

  // 监听prop width变化
  useEffect(() => {
    const newWidth = parseWidthToPx(propWidth);
    if (newWidth !== width) {
      setWidth(newWidth);
      currentWidthRef.current = newWidth;
    }
  }, [propWidth, width]);

  // 同步currentWidthRef与width状态
  useEffect(() => {
    currentWidthRef.current = width;
  }, [width]);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);

    // 添加body class来禁用文本选择
    document.body.classList.add('dragging-drawer');

    const startX = e.clientX;
    const startWidth = currentWidthRef.current;

    const handleMouseMove = (e) => {
      let newWidth;
      if (placement === 'right') {
        // 右侧抽屉：向左拖拽增加宽度，向右拖拽减少宽度
        newWidth = startWidth + (startX - e.clientX);
      } else {
        // 左侧抽屉：向右拖拽增加宽度，向左拖拽减少宽度
        newWidth = startWidth + (e.clientX - startX);
      }

      // 限制最小和最大宽度
      const minWidth = 280;
      const maxWidth = Math.min(window.innerWidth * 0.8);
      newWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));

      // 立即更新状态
      setWidth(newWidth);

      // 使用throttled回调来减少外部重新渲染频率
      if (throttledOnWidthChange) {
        throttledOnWidthChange(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.body.classList.remove('dragging-drawer');
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);

      // 拖拽结束时立即执行pending的throttled更新并调用最终回调
      if (throttledOnWidthChange && throttledOnWidthChange.flush) {
        throttledOnWidthChange.flush();
      }
      if (onWidthChange) {
        onWidthChange(currentWidthRef.current);
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [placement, onWidthChange, throttledOnWidthChange]);

  // 防止鼠标离开resize handle时拖拽状态丢失
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
        document.body.classList.remove('dragging-drawer');

        // 拖拽结束时立即执行pending的throttled更新并调用最终回调
        if (throttledOnWidthChange && throttledOnWidthChange.flush) {
          throttledOnWidthChange.flush();
        }
        if (onWidthChange) {
          onWidthChange(currentWidthRef.current);
        }
      }
    };

    if (isDragging) {
      document.addEventListener('mouseup', handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDragging, onWidthChange, throttledOnWidthChange]);

  // 清理throttled函数
  useEffect(() => {
    return () => {
      if (throttledOnWidthChange && throttledOnWidthChange.cancel) {
        throttledOnWidthChange.cancel();
      }
    };
  }, [throttledOnWidthChange]);

  const drawerWidth = typeof width === 'number' ? `${width}px` : width;

  // 如果禁用拖拽，则使用原始传入的宽度，和原生Drawer组件保持一致
  const finalWidth = disableDrag ? propWidth : drawerWidth;

  return (
    <Drawer
      open={open}
      {...otherProps}
      ref={drawerRef}
      placement={placement}
      width={finalWidth}
      className={`${_style.csDraggableDrawer} ${isDragging ? _style.csDraggableDrawerContainerNoTransition : ''} ${isDragging ? _style.dragging : ''} ${props.className || ''}`}
    >
      {/* 拖拽手柄 */}
      {!disableDrag && placement === 'right' && (
        <div
          ref={resizeHandleRef}
          className={`${_style.resizeHandle} ${_style.resizeHandleLeft} ${isDragging ? _style.dragging : ''}`}
          onMouseDown={handleMouseDown}
        />
      )}
      {!disableDrag && placement === 'left' && (
        <div
          ref={resizeHandleRef}
          className={`${_style.resizeHandle} ${_style.resizeHandleRight} ${isDragging ? _style.dragging : ''}`}
          onMouseDown={handleMouseDown}
        />
      )}

      {/* 原始内容 */}
      {props.children}
    </Drawer>
  );
};

export default CsDraggableDrawer;
